import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Clock, Croissant, Utensils, Waves, Pizza, Soup } from "lucide-react";
import { Img } from "@/components/common/Primitives";
import { minPrice } from "@/lib/content";
import { nok } from "@/lib/format";
import { SIZES } from "@/lib/images";
import { useLang } from "@/lib/i18n";

const NN_WEEKDAYS = ["søndag", "måndag", "tysdag", "onsdag", "torsdag", "fredag", "laurdag"];
const NN_MONTHS = ["januar", "februar", "mars", "april", "mai", "juni", "juli", "august", "september", "oktober", "november", "desember"];

/** Parse "17.00–21.00" / "17:00–21:00" -> [start, end] minutes; null if not a plain range */
const parseRange = (v) => {
  const m = /(\d{1,2})[.:](\d{2})\s*[–-]\s*(\d{1,2})[.:](\d{2})/.exec(v || "");
  return m ? [+m[1] * 60 + +m[2], +m[3] * 60 + +m[4]] : null;
};
const fmtClock = (min, lang) => `${String(Math.floor(min / 60)).padStart(2, "0")}${lang === "en" ? ":" : "."}${String(min % 60).padStart(2, "0")}`;

/**
 * Live status for the restaurant – derived strictly from the published hours, so it never claims
 * something the hours page does not. Breakfast + dinner (Sunday dinner on Sundays).
 */
export const OpenNow = ({ dining, testid = "home-open-now" }) => {
  const { lang, tx } = useLang();
  const hours = Object.fromEntries((dining?.hours || []).map((h) => [h.key, h.value]));
  const now = new Date();
  const dow = now.getDay();
  const mins = now.getHours() * 60 + now.getMinutes();
  const windows = [
    { label: tx("Frukost", "Breakfast"), range: parseRange(hours.breakfast || "07.30–10.00") },
    dow === 0
      ? { label: tx("Søndagsmiddag", "Sunday dinner"), range: parseRange(hours.sunday || "13.00–18.00") }
      : { label: tx("Middag", "Dinner"), range: parseRange(hours.dinner || "17.00–21.00") },
  ].filter((w) => w.range);
  const open = windows.find((w) => mins >= w.range[0] && mins < w.range[1]);
  const next = windows.find((w) => mins < w.range[0]);
  let text;
  let state;
  if (open) { text = tx(`Restauranten er open · ${open.label.toLowerCase()} til ${fmtClock(open.range[1], lang)}`, `Restaurant open · ${open.label.toLowerCase()} until ${fmtClock(open.range[1], lang)}`); state = "open"; }
  else if (next) { text = tx(`Restauranten opnar ${fmtClock(next.range[0], lang)} · ${next.label.toLowerCase()}`, `Restaurant opens ${fmtClock(next.range[0], lang)} · ${next.label.toLowerCase()}`); state = "soon"; }
  else { text = tx("Restauranten er stengt for i dag · frukost frå 07.30", "Restaurant closed for today · breakfast from 07:30"); state = "closed"; }
  const dot = state === "open" ? "bg-success" : state === "soon" ? "bg-warning" : "bg-muted-foreground/60";
  return (
    <p className="inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold text-foreground tabular" data-testid={testid} data-state={state}>
      <span className="relative flex h-2 w-2">
        {state === "open" && <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${dot} opacity-60`} aria-hidden />}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${dot}`} aria-hidden />
      </span>
      {text}
    </p>
  );
};

/** Hook shared by the card and the flat table: today's rows, computed from the published hours */
const useTodayRows = (dining) => {
  const { tx } = useLang();
  return useMemo(() => {
    const hours = Object.fromEntries((dining?.hours || []).map((h) => [h.key, h.value]));
    const now = new Date();
    const dow = now.getDay(); // 0 = Sunday
    const month = now.getMonth() + 1;
    const summer = month >= 6 && month <= 8;
    const weekendCafe = dow === 5 || dow === 6 || dow === 0;
    const list = [];
    list.push({ icon: Croissant, k: tx("Frukost", "Breakfast"), v: hours.breakfast || "07.30–10.00", sub: tx("Inkludert for hotellgjester", "Included for hotel guests") });
    if (dow === 0) list.push({ icon: Utensils, k: tx("Søndagsmiddag", "Sunday dinner"), v: hours.sunday || "13.00–18.00", sub: tx("Restauranten", "The restaurant") });
    else list.push({ icon: Utensils, k: tx("Middag", "Dinner"), v: hours.dinner || "17.00–21.00", sub: tx("Restauranten", "The restaurant") });
    if (dow === 4) list.push({ icon: Soup, k: tx("Komletorsdag", "Komle Thursday"), v: hours.komle || tx("Frå 13.00", "From 13:00"), sub: tx("Til det er tomt i grytene", "Until the pots are empty"), highlight: true });
    list.push({
      icon: Pizza,
      k: "Strandkaféen",
      v: summer ? hours["cafe-summer"] || "11.00–21.00" : weekendCafe ? hours["cafe-offseason"] || "12.00–20.00" : tx("Stengt i dag", "Closed today"),
      sub: summer ? tx("Kvar dag i sesongen", "Every day in season") : tx("Utanom sesong: fredag–søndag", "Off season: Friday–Sunday"),
    });
    list.push({ icon: Waves, k: tx("Bassenget", "The pool"), v: summer ? tx("Ope · 28°", "Open · 28°") : tx("Opnar i juni", "Opens in June"), sub: tx("Sjøkanten Trivsel · gratis for gjester", "Sjøkanten Trivsel · free for guests") });
    return list;
  }, [dining, tx]);
};

const useDateLabel = () => {
  const { lang } = useLang();
  const today = new Date();
  return lang === "en"
    ? new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long" }).format(today)
    : `${NN_WEEKDAYS[today.getDay()]} ${today.getDate()}. ${NN_MONTHS[today.getMonth()]}`;
};

/**
 * "Today at the hotel" as a flat hairline table – no card, no icon circles. Name and note on the
 * left, time on the right in tabular figures. Komle Thursday gets a quiet accent tint.
 */
export const TodayTable = ({ dining, testid = "home-today" }) => {
  const { tx } = useLang();
  const rows = useTodayRows(dining);
  const dateLabel = useDateLabel();
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="text-sm font-semibold text-foreground">{tx("I dag på hotellet", "Today at the hotel")}</h3>
        <p className="text-sm text-muted-foreground tabular first-letter:uppercase">{dateLabel}</p>
      </div>
      <ul className="mt-4 border-t border-border">
        {rows.map((r) => (
          <li key={r.k} className={`flex items-baseline justify-between gap-6 border-b border-border py-3.5 ${r.highlight ? "-mx-3 bg-accent/60 px-3" : ""}`} data-testid={`${testid}-${r.k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="min-w-0">
              <p className="text-[1.0625rem] font-semibold leading-tight tracking-[-0.01em] text-foreground">{r.k}</p>
              <p className="mt-0.5 text-sm leading-snug text-muted-foreground">{r.sub}</p>
            </div>
            <p className="shrink-0 text-[1.0625rem] font-medium tabular text-foreground">{r.v}</p>
          </li>
        ))}
      </ul>
      <Link to="/mat#opningstider" className="mt-4 inline-flex items-center gap-1.5 text-[0.9375rem] font-semibold text-foreground underline decoration-border underline-offset-[6px] transition-colors hover:decoration-foreground" data-testid={`${testid}-link`}>
        {tx("Alle opningstider", "All opening hours")} <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
};

/** Nearby places as a stacked hairline list – place left, distance right. Real places, real distances. */
export const NearbyList = ({ className = "", testid = "home-nearby" }) => {
  const { tx } = useLang();
  const items = [
    [tx("Kaien og småbåthamna", "The quay and marina"), "100 m"],
    [tx("Butikk og bakeri", "Shop and bakery"), tx("3 min å gå", "3 min walk")],
    [tx("Badeøya Peparen", "Peparen island"), tx("5 min å gå", "5 min walk")],
    [tx("Langfoss", "Langfoss waterfall"), tx("45 min med bil", "45 min by car")],
    [tx("Trolltunga", "Trolltunga"), tx("ca. 2 t med bil", "approx. 2 h by car")],
  ];
  return (
    <div className={className} data-testid={testid}>
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="text-sm font-semibold text-foreground">{tx("I nærleiken", "Nearby")}</h3>
        <p className="text-sm text-muted-foreground">{tx("Frå resepsjonen", "From reception")}</p>
      </div>
      <dl className="mt-4 border-t border-border">
        {items.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-6 border-b border-border py-3.5">
            <dt className="text-[1.0625rem] font-semibold leading-tight tracking-[-0.01em] text-foreground">{k}</dt>
            <dd className="shrink-0 text-[1.0625rem] font-medium tabular text-foreground">{v}</dd>
          </div>
        ))}
      </dl>
      <Link to="/praktisk#veg" className="mt-4 inline-flex items-center gap-1.5 text-[0.9375rem] font-semibold text-foreground underline decoration-border underline-offset-[6px] transition-colors hover:decoration-foreground" data-testid={`${testid}-link`}>
        {tx("Slik kjem du hit", "How to get here")} <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
};

/**
 * "Today at the hotel" – computed from the content hours/offers, never invented.
 * Weekday-aware (komle on Thursdays, Sunday dinner) and season-aware (café + pool June–August).
 */
export const TodayCard = ({ dining, testid = "home-today" }) => {
  const { tx } = useLang();
  const rows = useTodayRows(dining);
  const dateLabel = useDateLabel();

  return (
    <div className="rounded-[24px] bg-card p-6 ring-hairline sm:p-7" data-testid={testid}>
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm font-semibold text-foreground">{tx("I dag på hotellet", "Today at the hotel")}</p>
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground tabular"><Clock className="h-3.5 w-3.5" aria-hidden /> <span className="first-letter:uppercase">{dateLabel}</span></p>
      </div>
      <ul className="mt-5 divide-y divide-border">
        {rows.map((r) => {
          const I = r.icon;
          return (
            <li key={r.k} className={`flex items-center gap-4 py-3.5 ${r.highlight ? "-mx-3 rounded-2xl bg-accent/70 px-3" : ""}`} data-testid={`${testid}-${r.k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-foreground"><I className="h-4 w-4" strokeWidth={1.75} aria-hidden /></span>
              <div className="min-w-0 flex-1">
                <p className="text-[0.9375rem] font-semibold leading-tight text-foreground">{r.k}</p>
                <p className="mt-0.5 text-sm leading-snug text-muted-foreground">{r.sub}</p>
              </div>
              <p className="shrink-0 text-[0.9375rem] font-semibold tabular text-foreground">{r.v}</p>
            </li>
          );
        })}
      </ul>
      <Link to="/mat#opningstider" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-foreground underline decoration-border underline-offset-4 transition-colors hover:decoration-foreground" data-testid={`${testid}-link`}>
        {tx("Alle opningstider", "All opening hours")} <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
};

/** Small, plain "what's nearby" line – real places, real distances, no icon grid. */
export const NearbyLine = ({ className = "", testid = "home-nearby" }) => {
  const { tx } = useLang();
  const items = [
    [tx("Kaien", "The quay"), "100 m"],
    [tx("Butikk og bakeri", "Shop and bakery"), tx("3 min å gå", "3 min walk")],
    [tx("Badeøya Peparen", "Peparen island"), tx("5 min å gå", "5 min walk")],
    [tx("Langfoss", "Langfoss waterfall"), tx("45 min med bil", "45 min by car")],
    [tx("Trolltunga", "Trolltunga"), tx("ca. 2 t med bil", "approx. 2 h by car")],
  ];
  return (
    <dl className={`flex flex-wrap gap-x-7 gap-y-3 ${className}`} data-testid={testid}>
      {items.map(([k, v]) => (
        <div key={k} className="flex items-baseline gap-2">
          <dt className="text-sm text-muted-foreground">{k}</dt>
          <dd className="text-sm font-semibold text-foreground tabular">{v}</dd>
        </div>
      ))}
    </dl>
  );
};

/** Horizontal room row (photo left, name + specs + price right) – used beside the featured room on the home page */
export const RoomRow = ({ rt, testid, eager = false }) => {
  const { lang, tx, tr } = useLang();
  const id = testid || `room-row-${rt.id}`;
  return (
    <Link to={`/rom/${rt.slug}`} className="group grid grid-cols-[112px_minmax(0,1fr)_auto] items-center gap-4 border-t border-border py-4 transition-colors duration-200 first:border-t-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:grid-cols-[168px_minmax(0,1fr)_auto] sm:gap-6 sm:py-5" data-testid={id}>
      <Img src={rt.images?.[0]} alt={tr(rt.name)} className="img-zoom aspect-[4/3] w-full" radius="rounded-lg" sizes={SIZES.quarter} eager={eager} />
      <div className="min-w-0">
        <div className="flex items-start justify-between gap-4">
          <p className="text-[1.125rem] font-semibold leading-snug tracking-[-0.015em] text-foreground underline decoration-transparent decoration-1 underline-offset-[6px] transition-colors duration-200 group-hover:decoration-border">{tr(rt.name)}</p>
          <p className="shrink-0 pt-px text-right text-[1.0625rem] font-semibold tnum text-foreground" data-testid={`${id}-price`}>
            {nok(minPrice(rt), lang)} <span className="text-xs font-normal text-muted-foreground">/ {tx("natt", "night")}</span>
          </p>
        </div>
        <p className="mt-1 line-clamp-1 text-[0.9375rem] leading-snug text-muted-foreground">{tr(rt.tagline)}</p>
        <p className="mt-2.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-muted-foreground tabular">
          {rt.size_m2 && <span>{rt.size_m2} m²</span>}
          {rt.size_m2 && <span className="h-0.5 w-0.5 rounded-full bg-border" aria-hidden />}
          <span>{tx(`Inntil ${rt.max_guests} gjester`, `Up to ${rt.max_guests} guests`)}</span>
          {rt.dogs_allowed && <span className="h-0.5 w-0.5 rounded-full bg-border" aria-hidden />}
          {rt.dogs_allowed && <span>{tx("Hund velkomen", "Dogs welcome")}</span>}
        </p>
      </div>
      <ArrowUpRight className="h-5 w-5 text-muted-foreground transition-[transform,color] duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground" />
    </Link>
  );
};

/** Compact meeting-room row used on the home page */
export const MeetingRow = ({ m, tr, tx }) => (
  <Link to={`/arrangement#${m.id}`} className="group flex items-center justify-between gap-4 border-t border-border py-4 transition-colors hover:text-foreground" data-testid={`home-meeting-${m.id}`}>
    <div className="min-w-0">
      <p className="text-[1.0625rem] font-semibold leading-tight text-foreground">{m.name}</p>
      <p className="mt-0.5 truncate text-sm text-muted-foreground">{tr(m.tagline)}</p>
    </div>
    <div className="flex shrink-0 items-center gap-4 text-sm tabular text-muted-foreground">
      <span>{m.area_m2} m²</span>
      {m.capacity?.kino && <span className="hidden sm:inline">{tx("Kino", "Theatre")} {m.capacity.kino}</span>}
      {m.capacity?.styrebord && !m.capacity?.kino && <span className="hidden sm:inline">{tx("Styrebord", "Boardroom")} {m.capacity.styrebord}</span>}
      <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground" />
    </div>
  </Link>
);
